import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Route-based sub-nav for the Settings primary tab: Banks & processors / Sources
 * / Audit log. Contacts has left this drawer — it's working data, not
 * configuration, so it's a first-class primary tab now. Treasurers visit these
 * remaining settings surfaces rarely.
 */
type SettingsTab = {
  label: string;
  to: string;
  requireManage?: boolean;
  // Owner / platform-admin only (the same bar as suspending an account) — distinct from
  // Finance-Manage, which must NOT reach it (a terminal outcome auto-reins the recurring cutoff in).
  requireOwnerOrAdmin?: boolean;
  // Platform-admin ONLY — stricter than requireOwnerOrAdmin: a campaign's own owner is excluded. The
  // recurring wind-down (pull-in / stop-all) is a destructive campaign-wide sweep kept to platform staff.
  requirePlatformAdmin?: boolean;
};

const TABS: SettingsTab[] = [
  // Reframed from one-time "Setup" to the ongoing "Banks & processors" settings
  // surface (same route, same OnboardingPage component).
  { label: 'Banks & processors', to: '/admin/finance/settings/setup' },
  // Bank statement-parsing profiles. Ungated: the page does not self-gate, and
  // it sits alongside the ungated setup/sources config surfaces.
  { label: 'Bank accounts', to: '/admin/finance/settings/bank-accounts' },
  // Processor fee rates + settlement-aging thresholds. Manage-gated: editing
  // these values is compliance-sensitive, so the tab is hidden from users who
  // can't manage Finance (the route itself stays reachable by direct URL).
  { label: 'Processor settings', to: '/admin/finance/settings/processors', requireManage: true },
  { label: 'Sources', to: '/admin/finance/settings/sources' },
  // Read-only view of the jurisdiction's ruleset file. Manage-gated: the notes it surfaces
  // cite internal issues and ADRs — reference for whoever configures compliance, not a
  // view-only treasurer. The source endpoint enforces Finance.Manage itself.
  { label: 'Rules', to: '/admin/finance/settings/rules', requireManage: true },
  // ADR-069 (#616) — the recurring wind-down surface (pull-in / stop-all). Platform-admin ONLY: a
  // destructive campaign-wide sweep kept to platform staff, not a campaign's own owner or finance manager.
  { label: 'Recurring donations', to: '/admin/finance/settings/recurring', requirePlatformAdmin: true },
  // ADR-076 (#630) — record this campaign's election outcome. Owner / platform-admin only (the same
  // bar as suspending an account), NOT Finance-Manage — a terminal result auto-reins the cutoff in.
  {
    label: 'Election outcome',
    to: '/admin/finance/settings/election-outcome',
    requireOwnerOrAdmin: true,
  },
  { label: 'Audit log', to: '/admin/finance/settings/audit-log', requireManage: true },
];

export function FinanceSettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));
  const isOwner = useCampaignStore((s) => s.isOwner());
  const { user } = useAuth();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const canRecordOutcome = isOwner || isPlatformAdmin;

  return (
    <div className="w-full space-y-4">
      <nav
        data-testid="settings-sub-nav"
        className="flex gap-1 border-b border-border"
      >
        {TABS.map((tab) => {
          if (tab.requireManage && !canManageFinance) return null;
          if (tab.requireOwnerOrAdmin && !canRecordOutcome) return null;
          if (tab.requirePlatformAdmin && !isPlatformAdmin) return null;
          const isActive = pathname === tab.to || pathname.startsWith(`${tab.to}/`);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30',
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}
